import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon.js";
import { Slider } from "../components/Controls.js";
import { usePhone } from "../hooks/useMedia.js";
import { useEditor } from "./store.js";
import { cutAt, boundaryOf, type CutStyle } from "@hudhud/core";
import { startDrag } from "./timeline/drags.js";
import { CaptionGallery } from "./CaptionGallery.js";
import { SoundGallery } from "./SoundGallery.js";
import { PictureGallery } from "./PictureGallery.js";
import { CUT_STYLES, TIMED_CUTS, LOOKS, EFFECTS, isEffectOn, toggleEffect, type GalleryKind } from "./fxCatalog.js";
import { useStills, TransitionPreview, LookPreview, EffectPreview } from "./FxPreview.js";

/** preview height over tile width: portrait on the desk's narrow tiles; squatter on a phone, where two wide tiles must share a short sheet */
const PREVIEW_RATIO = { desk: 1.2, phone: 0.75 };

/** Measures the grid so every preview gets a whole number of pixels: two columns on a phone, three on the desk. */
const useGrid = (columns?: number) => {
  const phone = usePhone();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => { const cs = getComputedStyle(el); setWidth(el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)); };
    const ro = new ResizeObserver(measure); ro.observe(el); measure();
    return () => ro.disconnect();
  }, []);
  const cols = columns ?? (phone ? 2 : 3);
  const gap = phone ? 8 : 10; // the grid's own gap in theme.css
  const tileW = width ? Math.floor((width - (cols - 1) * gap) / cols) : 120;
  return { ref, previewH: Math.round(tileW * (phone ? PREVIEW_RATIO.phone : PREVIEW_RATIO.desk)), gridStyle: { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } };
};

/** One option: its preview, its name, and "In use" while the clip has it. A div, not a button: a button flex column collapsed the preview in Chrome. */
const Tile: React.FC<{ id: string; name: string; hint: string; on: boolean; onPick: () => void; children: React.ReactNode }> = ({ id, name, hint, on, onPick, children }) => (
  <div className={`gtile fxtile${on ? " on" : ""}`} role="button" tabIndex={0} data-fx={id} onClick={onPick} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } }} aria-pressed={on} aria-label={`${name}${on ? ", in use" : ""}`}>
    {children}
    <span className="gtile-name">{name}{on ? <b>In use</b> : null}</span>
    <span className="gtile-tags">{hint}</span>
  </div>
);

/**
 * Drag a transition tile onto the timeline: the nearest cut lights up as the pointer moves and the drop lands on it (a mouse only;
 * on a phone a cut is tapped first, then a tile). The tile's click is swallowed when the pointer went for a drag.
 */
function dragTransition(e: React.PointerEvent, style: CutStyle, label: string, didDrag: { current: boolean }): void {
  if (e.pointerType !== "mouse" || e.button !== 0) return;
  const st = useEditor.getState();
  let badge: HTMLDivElement | null = null;
  const nearestCut = (cx: number, cy: number): number | null => {
    const tl = document.querySelector<HTMLElement>(".tl");
    if (!tl) return null;
    const r = tl.getBoundingClientRect();
    if (cx < r.left - 24 || cx > r.right + 24 || cy < r.top - 40 || cy > r.bottom + 24) return null;
    let best: { i: number; d: number } | null = null;
    for (const el of document.querySelectorAll<HTMLElement>("[data-cut]")) { const b = el.getBoundingClientRect(); const d = Math.abs((b.left + b.right) / 2 - cx); if (!best || d < best.d) best = { i: Number(el.dataset.cut), d }; }
    return best && best.d < 120 ? best.i : null;
  };
  startDrag(e, {
    threshold: 6,
    move: (_dx, _dy, ev) => {
      didDrag.current = true;
      if (!badge) { badge = document.createElement("div"); badge.className = "cut-drag"; badge.textContent = label; document.body.appendChild(badge); }
      badge.style.transform = `translate(${ev.clientX + 14}px, ${ev.clientY - 14}px)`;
      const over = nearestCut(ev.clientX, ev.clientY);
      badge.classList.toggle("ready", over != null);
      const cur = useEditor.getState().cutDrag;
      if (!cur || cur.over !== over) st.setCutDrag({ style, over });
    },
    end: (moved) => {
      badge?.remove();
      const cur = useEditor.getState().cutDrag;
      st.setCutDrag(null);
      if (!moved) { didDrag.current = false; return; }
      if (cur?.over != null) { st.setCutTransition(cur.over, style); st.setSelectedCut(cur.over); }
    },
  });
}

/**
 * Every cut style, handing over between the clip's own stills. A tap sets the clip's own transition, or the selected cut's alone;
 * on the desk a tile can also be dragged onto the timeline, where it snaps to the nearest cut.
 */
export const TransitionGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const clip = useEditor((s) => s.clip);
  const update = useEditor((s) => s.update);
  const selectedCut = useEditor((s) => s.selectedCut);
  const phone = usePhone();
  const stills = useStills();
  const { ref, previewH, gridStyle } = useGrid(columns);
  const didDrag = useRef(false);
  if (!clip) return null;
  const cut = selectedCut != null && selectedCut >= -1 && selectedCut <= clip.segments.length - 1 ? selectedCut : null;
  const current = cut != null ? cutAt(clip, cut) : null;
  const what = cut == null ? "" : boundaryOf(clip, cut) === "opener" ? "The opener's hand-over into the video" : boundaryOf(clip, cut) === "end" ? "The video's hand-over into the end card" : `Cut ${cut + 1}`;
  const pick = (style: CutStyle) => { if (didDrag.current) { didDrag.current = false; return; } if (cut != null) useEditor.getState().setCutTransition(cut, style); else update((c) => ({ ...c, cutStyle: style })); };
  const ownCount = clip.cutTransitions.length;
  return (
    <div className="gallery">
      <div className="gallery-head">
        {cut != null ? <span className="hint"><b>{what} is selected:</b> tap a transition for it alone{current?.own ? "" : " (it plays the clip's own for now)"}. Tap the timeline's background to go back to the whole clip.</span>
          : <span className="hint">Tap one for every cut in this clip, the opener's hand-over and the end card's included{phone ? ", or tap a cut on the timeline first to give it a transition of its own" : ", or drag one onto the timeline: it snaps to the nearest cut"}.{ownCount ? ` ${ownCount} cut${ownCount > 1 ? "s have" : " has"} its own.` : ""}</span>}
        {cut != null && current?.own ? <button className="btn sm" onClick={() => useEditor.getState().setCutTransition(cut, null)}>Same as the clip</button> : null}
      </div>
      <div ref={ref} className="gallery-grid" style={gridStyle} data-mode={cut != null ? "cut" : "clip"}>
        {CUT_STYLES.map((o) => (
          <div key={o.value} className="fxdrag" onPointerDown={(e) => dragTransition(e, o.value, o.label, didDrag)} title={phone ? undefined : "Tap to use it, or drag it onto a cut on the timeline"}>
            <Tile id={o.value} name={o.label} hint={o.hint} on={cut != null ? current?.own === true && current.style === o.value : clip.cutStyle === o.value} onPick={() => pick(o.value)}>
              <TransitionPreview style={o.value} stills={stills} height={previewH} />
            </Tile>
          </div>
        ))}
      </div>
      {TIMED_CUTS.includes(clip.cutStyle) ? <div className="gallery-foot"><Slider label="Transition length" value={Math.round(clip.transitionMs / 10)} min={8} max={100} format={(v) => `${(v / 100).toFixed(2)}s`} onChange={(v) => update((c) => ({ ...c, transitionMs: v * 10 }))} /></div> : null}
    </div>
  );
};

/** Every colour grade over the clip's own still, at the clip's strength and vignette, so the tiles change as the sliders move. */
export const LookGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const clip = useEditor((s) => s.clip);
  const update = useEditor((s) => s.update);
  const { a: still } = useStills();
  const { ref, previewH, gridStyle } = useGrid(columns);
  if (!clip) return null;
  return (
    <div className="gallery">
      <div className="gallery-head"><span className="hint">The colour grade over the whole clip, shown on its own still. Strength and vignette below change every tile.</span></div>
      <div ref={ref} className="gallery-grid" style={gridStyle}>
        {LOOKS.map((l) => (
          <Tile key={l.value} id={l.value} name={l.label} hint={l.hint} on={clip.look.preset === l.value} onPick={() => update((c) => ({ ...c, look: { ...c.look, preset: l.value } }))}>
            <LookPreview preset={l.value} strength={clip.look.strength} vignette={clip.look.vignette} still={still} height={previewH} />
          </Tile>
        ))}
      </div>
      <div className="gallery-foot">
        {clip.look.preset !== "natural" ? <Slider label="Strength" value={Math.round(clip.look.strength * 100)} min={10} max={100} format={(v) => `${v}%`} onChange={(v) => update((c) => ({ ...c, look: { ...c.look, strength: v / 100 } }))} /> : null}
        <Slider label="Vignette" value={Math.round(clip.look.vignette * 100)} min={0} max={100} format={(v) => (v ? `${v}%` : "off")} onChange={(v) => update((c) => ({ ...c, look: { ...c.look, vignette: v / 100 } }))} />
      </div>
    </div>
  );
};

/** Every effect playing over the still. Each is a switch: tap to turn it on or off, and as many as you like can be on at once. */
export const EffectsGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const clip = useEditor((s) => s.clip);
  const update = useEditor((s) => s.update);
  const [q, setQ] = useState("");
  const { a: still } = useStills();
  const { ref, previewH, gridStyle } = useGrid(columns);
  if (!clip) return null;
  const needle = q.trim().toLowerCase();
  const list = EFFECTS.filter((e) => !needle || `${e.label} ${e.hint} ${e.keywords}`.toLowerCase().includes(needle));
  const onCount = EFFECTS.filter((e) => isEffectOn(clip.effects, e.key)).length;
  return (
    <div className="gallery">
      <div className="gallery-head">
        <div className="gallery-search"><Icon name="search" size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search effects… grain, flash, gold" aria-label="Search effects" /></div>
        <span className="hint">Tap to switch one on or off; several can be on at once. {onCount ? `${onCount} on.` : "None on."} Less is more.</span>
      </div>
      <div ref={ref} className="gallery-grid" style={gridStyle}>
        {list.map((e) => (
          <Tile key={e.key} id={e.key} name={e.label} hint={e.hint} on={isEffectOn(clip.effects, e.key)} onPick={() => update((c) => toggleEffect(c, e.key))}>
            <EffectPreview effect={e.key} still={still} height={previewH} />
          </Tile>
        ))}
        {!list.length ? <span className="hint" style={{ gridColumn: "1 / -1", padding: 20, textAlign: "center" }}>No effect matches that.</span> : null}
      </div>
      {/* how much grain, once it is on; the slider stops short of 0 so it cannot vanish under a drag — the tile switches grain off */}
      {clip.effects.grain > 0 ? <div className="gallery-foot"><Slider label="Film grain" value={Math.max(5, Math.round(clip.effects.grain * 100))} min={5} max={100} format={(v) => `${v}%`} onChange={(v) => update((c) => ({ ...c, effects: { ...c.effects, grain: v / 100 } }))} /></div> : null}
    </div>
  );
};

/** The gallery of one kind, as the settings column and the phone's sheet show it. */
export const GalleryBody: React.FC<{ kind: GalleryKind; columns?: number }> = ({ kind, columns }) =>
  kind === "captions" ? <CaptionGallery columns={columns} /> : kind === "transitions" ? <TransitionGallery columns={columns} /> : kind === "look" ? <LookGallery columns={columns} /> : kind === "sound" ? <SoundGallery columns={columns} /> : kind === "pictures" ? <PictureGallery columns={columns} /> : <EffectsGallery columns={columns} />;
